import { useState, useCallback, useMemo } from 'react'

import imgImportEmployees from '../../../common/images/onboarding/img_import_employees.png'
import imgVerifyDomains from '../../../common/images/onboarding/img_verify_domains.png'
import imgEnsureEmails from '../../../common/images/onboarding/img_ensure_emails.png'
import imgLaunchCampaign from '../../../common/images/onboarding/img_launch_campaign.png'
import { useNavigate } from 'react-router-dom'
import {
  useGetOnboardingStepsQuery,
  useCompleteOnboardingStepMutation,
  OnboardingStepType,
  OnboardingStepStatus
} from '../../../api/onboardingApiSlice'
import { useMutation } from '../../../hooks/useMutation'

export const DrawerType = {
  IMPORT_EMPLOYEES_DRAWER: 'IMPORT_EMPLOYEES_DRAWER',
  VERIFY_DOMAINS_DRAWER: 'VERIFY_DOMAINS_DRAWER',
  ENSURE_EMAILS_DRAWER: 'ENSURE_EMAILS_DRAWER',
  LAUNCH_CAMPAIGN_DRAWER: 'LAUNCH_CAMPAIGN_DRAWER',
  SKIP_LAUNCH_CAMPAIGN_ACTION: 'SKIP_LAUNCH_CAMPAIGN_ACTION'
}

const stepsUiConfig = [
  {
    id: 1,
    apiType: OnboardingStepType.IMPORT_EMPLOYEES,
    title: 'Import your employees',
    onboardingTitle: 'First Step',
    description:
      'Add your employees easily by connecting via Google, Outlook, Entra ID (Azure AD), or Okta, uploading a CSV file, or entering details manually.',
    previewImage: imgImportEmployees,
    actionButtonText: 'Add Employees',
    drawerType: DrawerType.IMPORT_EMPLOYEES_DRAWER,
    actionButtonStartIcon: 'PersonAddIcon',
    isRequired: true
  },
  {
    id: 2,
    apiType: OnboardingStepType.VERIFY_DOMAINS,
    title: 'Verify Your Domains',
    onboardingTitle: 'Second Step',
    description:
      "We'll send a verification email to the following addresses. Click the link in the email or  follow alternative instructions if needed.",
    previewImage: imgVerifyDomains,
    actionButtonText: 'Verify',
    drawerType: DrawerType.VERIFY_DOMAINS_DRAWER,
    isRequired: true,
    skipButtonText: 'Done'
  },
  {
    id: 3,
    apiType: OnboardingStepType.CONFIGURE_EMAIL_DELIVERY,
    title: 'Ensure Simulation Emails Are Delivered',
    onboardingTitle: 'Third Step',
    description:
      "Add our IPs and domains to your system's allowlist to ensure phishing simulations  aren't flagged as spam.",
    previewImage: imgEnsureEmails,
    actionButtonText: 'Get Started',
    drawerType: DrawerType.ENSURE_EMAILS_DRAWER,
    isRequired: true,
    skipButtonText: 'Done'
  },
  {
    id: 4,
    apiType: OnboardingStepType.RUN_PHISHING_DRILL,
    title: 'Launch your first campaign',
    onboardingTitle: 'Fourth Step',
    description:
      "Launch a one-time phishing drill to assess your team's security awareness. This test helps establish a baseline vulnerability rate, identifying potential risks and areas for improvement.",
    previewImage: imgLaunchCampaign,
    actionButtonText: 'Launch',
    drawerType: DrawerType.LAUNCH_CAMPAIGN_DRAWER,
    secondaryActionButtonText: 'Skip',
    secondaryActionDrawerType: DrawerType.SKIP_LAUNCH_CAMPAIGN_ACTION,
    isRequired: false
  }
]

export function useOnboardingStack() {
  const navigate = useNavigate()
  const { data: apiSteps, isLoading: isLoadingApiSteps, refetch: refetchApiSteps } = useGetOnboardingStepsQuery()

  const [activeDrawerType, setActiveDrawerType] = useState(null)

  const { submit: completeStepApiCall, isLoading: isCompletingStep } = useMutation(useCompleteOnboardingStepMutation, {
    showSuccessToast: false,
    onSuccess: async () => {
      await refetchApiSteps()
    },
    onError: err => {
      console.error('Failed to complete step (via useMutation):', err)
    }
  })

  const processedCards = useMemo(() => {
    let previousRequiredStepIncomplete = false
    return stepsUiConfig
      .map(uiStep => {
        const apiDataForStep = apiSteps?.find(apiStep => apiStep.type === uiStep.apiType)
        const status = apiDataForStep?.status || OnboardingStepStatus.IN_PROGRESS
        const completed = status === OnboardingStepStatus.COMPLETED

        let isDisabled = false
        if (previousRequiredStepIncomplete) {
          isDisabled = true
        }
        if (uiStep.isRequired && !completed) {
          previousRequiredStepIncomplete = true
        } else if (uiStep.isRequired && completed) {
          previousRequiredStepIncomplete = false
        }

        return {
          ...uiStep,
          completed,
          status,
          isDisabled
        }
      })
      .sort((a, b) => a.id - b.id)
  }, [apiSteps])

  const currentStepDetails = useMemo(() => {
    if (!activeDrawerType) return null
    return processedCards.find(
      step =>
        step.drawerType === activeDrawerType ||
        (step.secondaryActionDrawerType && step.secondaryActionDrawerType === activeDrawerType)
    )
  }, [activeDrawerType, processedCards])

  const isOnboardingCompleted = useMemo(() => {
    if (!apiSteps || apiSteps.length === 0) return false
    return processedCards.every(card => !card.isRequired || card.completed)
  }, [apiSteps, processedCards])

  const toggleShowAllStepsView = useCallback(() => {
    navigate('/learn')
  }, [navigate])

  const openDrawer = useCallback(drawerType => {
    if (!drawerType) {
      return
    }
    setActiveDrawerType(drawerType)
  }, [])

  const closeDrawer = useCallback(() => {
    if (activeDrawerType !== null) {
      setActiveDrawerType(null)
    }
    refetchApiSteps()
  }, [activeDrawerType])

  const handleCompleteStep = useCallback(
    async stepApiType => {
      try {
        await completeStepApiCall({ type: stepApiType })
        setActiveDrawerType(null)
      } catch (err) {
        /* Errors handled by useMutation's onError */
      }
    },
    [completeStepApiCall, setActiveDrawerType]
  )

  return {
    allCards: processedCards,
    visibleCards: processedCards.filter(card => card.status !== OnboardingStepStatus.COMPLETED),
    toggleShowAllStepsView,
    activeDrawerType,
    openDrawer,
    closeDrawer,
    handleCompleteStep,
    currentStepDetails,
    isOnboardingCompleted,
    isLoadingSteps: isLoadingApiSteps || isCompletingStep,
    refetchSteps: refetchApiSteps,
    OnboardingStepType
  }
}
